import './input-upload.scss';

import { filter, first, kebabCase, map } from 'lodash';
import {
  ChangeEvent,
  ComponentPropsWithRef,
  FocusEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { formatTestId } from '../../../data/utils/string.utils';
import {
  FileItemData,
  getFileDataFromFile,
  isValidFileSize,
  isValidFileType,
} from '../../../data/utils/upload.utils';
import { BaseInput, BaseInputIcon } from './base-input';

type InputUploadProps = Omit<
  ComponentPropsWithRef<typeof BaseInput>,
  'value' | 'onChange' | 'onBlur'
> & {
  type?: string;
  className?: string;
  label?: JSX.Element | string;
  description?: JSX.Element | string;
  prefix?: JSX.Element | string;
  suffix?: JSX.Element | string;
  icon?: BaseInputIcon;
  error?: string | { error: string; [key: string]: string };
  fileTypes: string[];
  fileSize: string;
  multiple?: boolean;
  value?: FileItemData | null;
  onChange?: (
    value: FileItemData | FileItemData[] | null,
    e: ChangeEvent<HTMLElement>
  ) => void;
  onBlur?: (
    value: FileItemData | FileItemData[] | null,
    e: FocusEvent<HTMLElement>
  ) => void;
};

export function InputUpload({
  testId,
  className,
  label,
  description,
  prefix,
  suffix,
  icon = BaseInputIcon.FILE,
  fileTypes = [],
  fileSize = '2MB',
  multiple = false,
  onChange,
  onBlur,
  ...props
}: InputUploadProps) {
  const intl = useIntl();

  const getTestId = useCallback(
    (testIdType: string | string[] | null = null) =>
      formatTestId('input-upload', testId ?? props.name, testIdType),
    [testId, props.name]
  );

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [value, setValue] = useState<FileItemData | null>(null);
  const [error, setError] = useState<
    string | { error: string; [key: string]: string } | null
  >(null);

  useEffect(() => {
    setValue(props.value ?? null);
  }, [props.value]);

  useEffect(() => {
    setError(props.error ?? null);
  }, [props.error]);

  const getFile = useCallback(
    async (file: File | null | undefined) => {
      let data: FileItemData | null = null;

      if (file) {
        if (!isValidFileType(file?.name, fileTypes)) {
          setError(
            intl.formatMessage({
              id: 'common.file-upload.error.type',
            })
          );
        } else if (!isValidFileSize(file?.size, fileSize)) {
          setError(
            intl.formatMessage({
              id: 'common.file-upload.error.size',
            })
          );
        } else {
          data = await getFileDataFromFile(file);
        }
      }

      return data;
    },
    [fileTypes, fileSize, intl]
  );

  const onHandleFiles = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const filesData = filter(
        await Promise.all(
          map((e.currentTarget as HTMLInputElement).files, file =>
            getFile(file)
          )
        ),
        file => file
      ).map(file => file as FileItemData);

      if (!multiple) {
        const fileData = first(filesData) ?? null;

        setValue(fileData);

        onChange?.(filesData, e as unknown as ChangeEvent<HTMLElement>);

        onBlur?.(filesData, e as unknown as FocusEvent<HTMLElement>);
      } else {
        setValue(null);

        onChange?.(filesData, e as unknown as ChangeEvent<HTMLElement>);

        onBlur?.(filesData, e as unknown as FocusEvent<HTMLElement>);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [getFile, onChange, onBlur, multiple, fileInputRef]
  );

  return (
    <div
      data-test-id={getTestId()}
      className={`form-group form-group--file${
        className ? ` form-group--${className}` : ''
      }${error ? ' invalid' : ''}${props.disabled ? ' disabled' : ''}`}
    >
      {label && (
        <label data-test-id={getTestId('label')} htmlFor={props.name}>
          {label}
        </label>
      )}

      {description && (
        <div
          data-test-id={getTestId('description')}
          className="input-description"
        >
          {description}
        </div>
      )}

      <div className="input-group">
        {prefix && (
          <span
            data-test-id={getTestId('prefix')}
            className="form-control-prefix"
          >
            {prefix}
          </span>
        )}
        {suffix && (
          <span
            data-test-id={getTestId('suffix')}
            className="form-control-suffix"
          >
            {suffix}
          </span>
        )}

        {icon !== BaseInputIcon.NONE && (
          <i
            data-test-id={getTestId('icon')}
            className={`icon ${kebabCase(icon)}`}
          />
        )}

        <input
          data-test-id={getTestId('input')}
          type="file"
          name={props.name}
          accept={fileTypes.join(',')}
          multiple={multiple}
          onChange={onHandleFiles}
          ref={fileInputRef}
        />

        <BaseInput
          data-test-id={getTestId('input-file')}
          name={`${props.name}-file`}
          defaultValue={value?.name}
          placeholder={
            props.placeholder ??
            intl.formatMessage({ id: 'common.file-upload.placeholder' })
          }
        />

        {error && (
          <div data-test-id={getTestId('error')} className="input-error">
            {intl.formatMessage(
              {
                id: typeof error === 'string' ? error : error.error,
              },
              typeof error === 'string' ? {} : error
            )}
          </div>
        )}
      </div>
    </div>
  );
}
